/**
 * @module Pages/AdminPages/AdminComponents/TotalNumberOfPartnersCard
 * @description This module contains the `TotalNumberOfPartnersCard` component, which fetches and displays 
 * the total number of partners (advisors). It shows the count in a dashboard card, and provides navigation 
 * to a detailed partners page when clicked.
 */
import { Skeleton } from '@mui/material';
import DashboardCard from 'components/commonComponents/cards/DashboardCard';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTotalPartners } from 'services/adminService';

/**
 * The `TotalNumberOfPartnersCard` component fetches the total number of partners (advisors) 
 * from the backend and displays the count in a dashboard card. The card is clickable, and 
 * when clicked, it navigates to a detailed page listing the partners.
 * 
 * @component
 * @returns {JSX.Element} A dashboard card displaying the total number of partners.
 */
const TotalNumberOfPartnersCard = () => {
  const [partnersData, setPartnersData] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  /**
   * Fetches the total number of partners data from the backend and 
   * updates the state.
   * 
   * This function is executed inside the `useEffect` hook to fetch data once when the component mounts.
   * 
   * @async
   * @function
   * @returns {void}
   */
  useEffect(() => {
    const fetchTotalNumberOfPartners = async () => {
      try {
        const response = await fetchTotalPartners();
        const { totalPartners } = response.data;
        setPartnersData({ totalPartners });
      } catch (error) {
        console.error('Error fetching number of partners data:', error);
      } finally {
        setLoading(false); // Set loading to false after API call
      }
    };

    fetchTotalNumberOfPartners();
  }, []);
  
  /**
   * Handles the click event on the dashboard card. Redirects the user to the detailed page for partners.
   * 
   * @function
   * @returns {void}
   */
  const handleClick = () => {
    navigate('/admin/partnerDetails', { state: { fromAdminDashboard: true } });
  };

  return (
    <>
      {
        loading ? (
          <Skeleton variant="rectangular" height={135} sx={{ borderRadius: '1.25rem' }} />
        ) : (
          <DashboardCard
            title="Total No of Advisors/Partners"
            subtitle={(partnersData.totalPartners ?? '--') || 0}
            handleClick={handleClick}
            isClickable
          />
        )
      }
    </>
  );
};

export default TotalNumberOfPartnersCard;
